import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * The leading "included in the service" checkbox for a source row in the coverage breakdown. Checked means
 * the governing service admits this source (so its traffic is covered); toggling it includes or excludes
 * the source from the service. Presentational: the checked state and the toggle callback come from the
 * content component (the lib passes inputs only, so the handler is a callback input).
 */
@Component({
    selector: 'app-coverage-include-cell',
    standalone: true,
    imports: [CommonModule],
    template: `
        <label
            class="cic"
            [class.cic--on]="checked || indeterminate"
            [class.cic--off]="disabled"
            (click)="$event.stopPropagation()"
        >
            <input
                type="checkbox"
                class="cic__input"
                [checked]="checked"
                [disabled]="disabled"
                [attr.aria-label]="checked ? 'Exclude from service' : 'Include in service'"
                (change)="toggle($event)"
            />
            <span class="cic__box">
                @if (checked) {
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
                        stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                } @else if (indeterminate) {
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
                        stroke-width="3.2" stroke-linecap="round"><path d="M6 12h12" /></svg>
                }
            </span>
        </label>
    `,
    styles: [
        `
            .cic {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }
            .cic--off {
                cursor: default;
                opacity: 0.4;
            }
            .cic__input {
                position: absolute;
                width: 1px;
                height: 1px;
                opacity: 0;
                pointer-events: none;
            }
            .cic__box {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 1rem;
                height: 1rem;
                border: 1.5px solid var(--border-color-strong, #888f9d);
                border-radius: 0.25rem;
                color: var(--offWhite_ALWAYS, #fff);
                background: var(--navigation, #fff);
                transition: background-color 0.1s ease, border-color 0.1s ease;
            }
            .cic--on .cic__box {
                background: var(--primaryColor, #0273fb);
                border-color: var(--primaryColor, #0273fb);
            }
            .cic:not(.cic--off):hover .cic__box {
                border-color: var(--primaryColor, #0273fb);
            }
        `,
    ],
})
export class CoverageIncludeCellComponent {
    @Input() checked = false;
    /** A parent only some of whose sources are included: show a dash rather than a check. */
    @Input() indeterminate = false;
    @Input() disabled = false;
    @Input() onToggle: () => void = () => undefined;

    toggle(event: Event): void {
        event.stopPropagation();
        if (!this.disabled) {
            this.onToggle();
        }
    }
}
